import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Plus, Edit, Trash2, Search, RefreshCw, 
  AlertCircle, X, Check, XCircle, MapPin, User, Phone, Mail,
  Calendar, FileText
} from 'lucide-react';
import { contractorLabsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ContractorLabsManagement = () => {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    lab_type: 'lab',
    supervisor_name: '',
    supervisor_email: '',
    supervisor_mobile: '',
    city: '',
    address: '',
    contract_start_date: '',
    contract_end_date: '',
    notes: '',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Cities list
  const cities = [
    'الرياض', 'جدة', 'مكة المكرمة', 'المدينة المنورة', 'الدمام',
    'الخبر', 'الظهران', 'تبوك', 'أبها', 'الطائف', 'بريدة', 'نجران',
    'جازان', 'حائل', 'الجبيل', 'ينبع', 'خميس مشيط', 'القطيف'
  ];

  const labTypes = [
    { value: 'hospital', label: 'مستشفى' },
    { value: 'lab', label: 'مختبر' },
    { value: 'clinic', label: 'عيادة' }
  ];

  const fetchLabs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await contractorLabsAPI.getAll({ limit: 100, search: searchQuery });
      const data = response.data?.data?.items || response.data?.items || response.data?.data || [];
      setLabs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('حدث خطأ في تحميل البيانات');
      setLabs([]);
    }
    setLoading(false);
  }, [searchQuery]);

  useEffect(() => { fetchLabs(); }, [fetchLabs]);

  const resetForm = () => {
    setFormData({
      name: '',
      lab_type: 'lab',
      supervisor_name: '',
      supervisor_email: '',
      supervisor_mobile: '',
      city: '',
      address: '',
      contract_start_date: '',
      contract_end_date: '',
      notes: '',
      active: true
    });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'اسم المختبر مطلوب';
    if (!formData.supervisor_name.trim()) errors.supervisor_name = 'اسم المشرف مطلوب';
    if (!formData.supervisor_email?.trim()) {
      errors.supervisor_email = 'البريد الإلكتروني مطلوب — سيتم إرسال بيانات الدخول إليه';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.supervisor_email)) {
      errors.supervisor_email = 'البريد الإلكتروني غير صحيح';
    }
    if (!formData.supervisor_mobile.trim()) {
      errors.supervisor_mobile = 'رقم الجوال مطلوب';
    } else if (!/^05\d{8}$/.test(formData.supervisor_mobile.replace(/\s/g, ''))) {
      errors.supervisor_mobile = 'رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام';
    }
    if (!formData.city) errors.city = 'المدينة مطلوبة';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (lab) => {
    setSelectedLab(lab);
    setFormData({
      name: lab.name || '',
      lab_type: lab.lab_type || 'lab',
      supervisor_name: lab.supervisor_name || '',
      supervisor_email: lab.supervisor_email || '',
      supervisor_mobile: lab.supervisor_mobile || '',
      city: lab.city || '',
      address: lab.address || '',
      contract_start_date: lab.contract_start_date ? lab.contract_start_date.split('T')[0] : '',
      contract_end_date: lab.contract_end_date ? lab.contract_end_date.split('T')[0] : '',
      notes: lab.notes || '',
      active: lab.active !== false
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      if (showEditModal && selectedLab) {
        await contractorLabsAPI.update(selectedLab._id, formData);
        toast.success('تم تحديث المختبر بنجاح');
        setShowEditModal(false);
        resetForm();
      } else {
        const res = await contractorLabsAPI.create(formData);
        const data = res.data?.data || res.data;
        setShowAddModal(false);
        resetForm();
        // Show credentials modal
        if (data?.generatedPassword) {
          setGeneratedCredentials({
            labName: formData.name,
            email: formData.supervisor_email,
            password: data.generatedPassword,
          });
          setShowCredentialsModal(true);
        } else {
          toast.success('تم إضافة المختبر بنجاح');
        }
      }
      await fetchLabs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedLab) return;
    setDeleting(true);
    try {
      await contractorLabsAPI.delete(selectedLab._id);
      setLabs(prev => prev.filter(l => l._id !== selectedLab._id));
      toast.success('تم حذف المختبر بنجاح');
      setShowDeleteModal(false);
      setSelectedLab(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء الحذف');
    }
    setDeleting(false);
  };

  const filteredLabs = labs.filter(lab => 
    lab.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lab.supervisor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lab.city?.includes(searchQuery)
  );

  const getLabTypeLabel = (type) => {
    const found = labTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-SA-u-ca-gregory');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">المختبرات المتعاقدة</h1>
              <p className="text-slate-500 text-sm mt-1">
                إجمالي <span className="font-semibold text-slate-700">{labs.length}</span> مختبر
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            <span>إضافة مختبر</span>
          </motion.button>
        </motion.div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="البحث باسم المختبر أو المشرف..."
                className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
              />
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <button 
              onClick={fetchLabs} 
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700"
          >
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="mr-auto"><X className="w-4 h-4" /></button>
          </motion.div>
        )}

        {/* Labs Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filteredLabs.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-20 text-center">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">لا توجد مختبرات متعاقدة</p>
            <button onClick={handleAdd} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl">
              إضافة مختبر
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLabs.map((lab, index) => (
              <motion.div
                key={lab._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800">{lab.name}</h3>
                        <span className="text-xs text-slate-500">{getLabTypeLabel(lab.lab_type)}</span>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      lab.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {lab.active ? 'نشط' : 'غير نشط'}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{lab.supervisor_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span dir="ltr">{lab.supervisor_mobile}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{lab.city}</span>
                    </div>
                    {lab.contract_end_date && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>ينتهي: {formatDate(lab.contract_end_date)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-100 p-3 flex gap-2">
                  <button
                    onClick={() => handleEdit(lab)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <Edit className="w-4 h-4" />
                    تعديل
                  </button>
                  <button
                    onClick={() => { setSelectedLab(lab); setShowDeleteModal(true); }}
                    className="flex items-center justify-center px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {(showAddModal || showEditModal) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
              >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 bg-[#1E293B]">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-white">
                      {showEditModal ? 'تعديل المختبر' : 'إضافة مختبر جديد'}
                    </h2>
                    <button
                      onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                      className="text-white/70 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Form */}
                <div className="p-6 overflow-y-auto flex-1">
                  <div className="space-y-5">
                    {/* Name & Type */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          اسم المختبر / المستشفى <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            formErrors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                          placeholder="مستشفى الملك فهد"
                        />
                        {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">نوع المنشأة</label>
                        <select
                          value={formData.lab_type}
                          onChange={(e) => setFormData({ ...formData, lab_type: e.target.value })}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                        >
                          {labTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Supervisor Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          اسم المشرف <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.supervisor_name}
                          onChange={(e) => setFormData({ ...formData, supervisor_name: e.target.value })}
                          className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            formErrors.supervisor_name ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                          placeholder="محمد أحمد"
                        />
                        {formErrors.supervisor_name && <p className="text-red-500 text-xs mt-1">{formErrors.supervisor_name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">البريد الإلكتروني <span className="text-red-500">*</span></label>
                        <input
                          type="email"
                          value={formData.supervisor_email}
                          onChange={(e) => setFormData({ ...formData, supervisor_email: e.target.value })}
                          className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            formErrors.supervisor_email ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                          placeholder="email@example.com"
                          dir="ltr"
                        />
                        {formErrors.supervisor_email && <p className="text-red-500 text-xs mt-1">{formErrors.supervisor_email}</p>}
                        {!showEditModal && <p className="text-emerald-600 text-xs mt-1">📧 سيتم إرسال رابط الدخول وبيانات الدخول لهذا البريد</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          رقم الجوال <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          value={formData.supervisor_mobile}
                          onChange={(e) => setFormData({ ...formData, supervisor_mobile: e.target.value })}
                          className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            formErrors.supervisor_mobile ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                          placeholder="05xxxxxxxx"
                          dir="ltr"
                        />
                        {formErrors.supervisor_mobile && <p className="text-red-500 text-xs mt-1">{formErrors.supervisor_mobile}</p>}
                      </div>
                    </div>

                    {/* Location */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          المدينة <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white ${
                            formErrors.city ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                        >
                          <option value="">اختر المدينة</option>
                          {cities.map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                        {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">العنوان</label>
                        <input
                          type="text"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          placeholder="حي النخيل، شارع الملك فهد"
                        />
                      </div>
                    </div>

                    {/* Contract Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">تاريخ بداية العقد</label>
                        <input
                          type="date"
                          value={formData.contract_start_date}
                          onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">تاريخ نهاية العقد</label>
                        <input
                          type="date"
                          value={formData.contract_end_date}
                          onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">ملاحظات</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
                        placeholder="ملاحظات إضافية..."
                      />
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="active"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="active" className="text-sm font-medium text-slate-700">
                        المختبر نشط
                      </label>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                  <button
                    onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                    className="px-5 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                    <span>{showEditModal ? 'حفظ التغييرات' : 'إضافة'}</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Modal */}
        <AnimatePresence>
          {showDeleteModal && selectedLab && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
              >
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">حذف المختبر</h3>
                  <p className="text-slate-500 mb-6">
                    هل أنت متأكد من حذف "{selectedLab.name}"؟<br />
                    سيتم حذف جميع البيانات المرتبطة به.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50"
                    >
                      إلغاء
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {deleting ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-5 h-5" />
                          <span>حذف</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Credentials Modal — shown after creating contractor lab */}
        <AnimatePresence>
          {showCredentialsModal && generatedCredentials && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCredentialsModal(false)}>
              <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
                <div className="text-center mb-5">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">تم إضافة المختبر بنجاح</h3>
                  <p className="text-sm text-gray-500 mt-1">تم إنشاء حساب دخول وإرسال البيانات للبريد الإلكتروني</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-3 mb-5">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">المختبر</span>
                    <span className="text-sm font-bold text-gray-800">{generatedCredentials.labName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">البريد الإلكتروني</span>
                    <span className="text-sm font-mono text-gray-800" dir="ltr">{generatedCredentials.email}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">كلمة المرور</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded" dir="ltr">{generatedCredentials.password}</span>
                      <button
                        onClick={() => { navigator.clipboard?.writeText(generatedCredentials.password); toast.success('تم نسخ كلمة المرور'); }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >نسخ</button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">رابط الدخول</span>
                    <span className="text-xs font-mono text-gray-600" dir="ltr">listechs.com/contractor/login</span>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5">
                  <p className="text-xs text-amber-800">⚠️ سيُطلب من المختبر المتعاقد تغيير كلمة المرور عند أول تسجيل دخول</p>
                </div>

                <button
                  onClick={() => setShowCredentialsModal(false)}
                  className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  تم
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ContractorLabsManagement;
